import Dialog from '@mui/material/Dialog';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useCreateUsersMutation } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { CreateUserForm } from '@/components/organisms/CreateUserForm';
import { CreateUserFormData, CreateUserRequest } from '@/DTOs/users';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { authSelector } from '@/StateManagement/AuthReducer';
import { isFetchError } from '@/utility/reactToolkit';

export const InviteUserModal: React.FC = (): React.ReactElement => {
  const { user } = useSelector(authSelector);

  const [conflictError, setConflictError] = React.useState<
    'user.conflict' | 'activeUser.conflict' | null
  >(null);
  const [createUsers] = useCreateUsersMutation();

  const toast = useToast();
  const tr = useTranslator();
  const navigate = useNavigate();

  const onClose = React.useCallback((): void => {
    navigate('..');
  }, [navigate]);

  const onSubmit = React.useCallback(
    async (formData: CreateUserFormData): Promise<void> => {
      if (!user?.integratorId) {
        toast.show(tr('MISSING_INTEGRATOR_INFORMATION'), 'error');
        return;
      }

      const createUserRequest: CreateUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        department: formData.department,
        integratorId: user.integratorId,
        type: 'INTEGRATOR',
        active: false,
        group: formData.group,
      };

      try {
        setConflictError(null);

        await createUsers(createUserRequest).unwrap();

        toast.show(tr('CREATE_USER_SUCCESS'), 'success');
        onClose();
      } catch (error) {
        if (isFetchError(error)) {
          const { data } = error;

          switch (error.status) {
            case 409:
              if (data.code === 'user.conflict' || data.code === 'activeUser.conflict') {
                setConflictError(data.code);
              } else {
                toast.show(tr('CREATE_USER_FAILED'), 'error');
              }
              break;
            default:
              console.error('Error creating user:', error);
              toast.show(tr('CREATE_USER_FAILED'), 'error');
              break;
          }
        } else {
          console.error('Error creating user:', error);
          toast.show(tr('CREATE_USER_FAILED'), 'error');
        }
      }
    },
    [createUsers, onClose, toast, tr, user?.integratorId],
  );

  return (
    <Dialog open={true}>
      <ApplicationModal
        title={tr('INVITE_A_TEAM_MEMBER')}
        variant="prominent"
        description={tr('FILL_OUT_DETAILS_FOR_NEW_MEMBER')}
        onClose={onClose}>
        <CreateUserForm error={conflictError} onSubmit={onSubmit} />
      </ApplicationModal>
    </Dialog>
  );
};
